import React, { useEffect, useRef, useState, useCallback } from "react";

import ReactMarkdown from "react-markdown";
import remarkGfm from "remark-gfm";
import { Prism as SyntaxHighlighter } from "react-syntax-highlighter";

import AuthPilot from "../images/image_AuthPilot.png";

import { useAuth } from "../auth/auth-provider";

// chat context
import { useChat } from "../auth/chat-provider";

const ChatConversation = ({ botIsLoading }) => {
  const { user } = useAuth();

  const { messages } = useChat();

  const messagesEndRef = useRef(null);
  const [typingMessage, setTypingMessage] = useState("");
  const [isTyping, setIsTyping] = useState(false);

  useEffect(() => {
    messagesEndRef.current?.scrollIntoView({ behavior: "smooth" });
  }, [messages]);

  useEffect(() => {
    if (
      !botIsLoading &&
      messages.length > 0 &&
      messages[messages.length - 1].role === "bot"
    ) {
      const message = messages[messages.length - 1]?.content;
      setTypingMessage("");
      setIsTyping(true);

      let i = 0;
      const typeInterval = setInterval(() => {
        if (i < message?.length) {
          setTypingMessage((prev) => prev + message[i]);
          i++;
        } else {
          setIsTyping(false);
          clearInterval(typeInterval);
        }
      }, 30);

      return () => clearInterval(typeInterval);
    }
  }, [botIsLoading, messages]);

  const [userImage, setUserImage] = useState("");

  const fetchUserData = useCallback(async () => {
    const fallbackImage = await import("../images/consiness-Raute.png");
    setUserImage(fallbackImage.default);
  }, []);

  useEffect(() => {
    fetchUserData();
  }, [user, fetchUserData]);

  return (
    <div
      className="p-4"
      style={{ overflowY: "auto", height: "calc(100vh - 150px)" }}
    >
      {messages.map((message, index) => {
        const isLastBotMessage =
          index === messages.length - 1 && message.role === "bot";

        return (
          <div
            key={index}
            className={`mb-3 d-flex align-items-center ${
              message.role === "user" ? "justify-content-end" : ""
            }`}
          >
            {message.role === "user" ? (
              <>
                <div className="bg-light p-3 rounded border d-flex align-items-center message-container">
                  {message.content}
                </div>
                <div
                  style={{
                    width: "50px",
                    height: "50px",
                    borderRadius: "50%",
                    marginLeft: "10px",
                  }}
                >
                  <img
                    src={userImage}
                    alt="User Avatar"
                    className="ms-2"
                    style={{
                      width: "50px",
                      height: "50px",
                      borderRadius: "50%",
                      objectFit: "contain",
                    }}
                  />
                </div>
              </>
            ) : (
              <>
                <div
                  style={{
                    width: "50px",
                    height: "50px",
                    borderRadius: "50%",
                    marginRight: "10px",
                  }}
                >
                  <img
                    src={AuthPilot}
                    alt="ChatGPT Avatar"
                    className="me-2"
                    style={{
                      width: "50px",
                      height: "50px",
                      borderRadius: "50%",
                      objectFit: "contain",
                    }}
                  />
                </div>
                <div
                  className="bg-white border rounded p-4 message-container"
                  style={{ maxWidth: "80%" }}
                >
                  <div className="bot-message markdown-body">
                    <ReactMarkdown
                      remarkPlugins={[remarkGfm]}
                      components={{
                        code({ node, inline, className, children, ...props }) {
                          const match = /language-(\w+)/.exec(className || "");
                          return !inline && match ? (
                            <SyntaxHighlighter
                              language={match[1]}
                              PreTag="div"
                              {...props}
                            >
                              {String(children).replace(/\n$/, "")}
                            </SyntaxHighlighter>
                          ) : (
                            <code className={className} {...props}>
                              {children}
                            </code>
                          );
                        },
                      }}
                    >
                      {isLastBotMessage && isTyping
                        ? typingMessage
                        : message.content}
                    </ReactMarkdown>
                  </div>
                </div>
              </>
            )}
          </div>
        );
      })}
      {botIsLoading && (
        <div className="d-flex align-items-center">
          <div
            style={{
              width: "50px",
              height: "50px",
              borderRadius: "50%",
              marginRight: "10px",
            }}
          >
            <img
              src={AuthPilot}
              alt="ChatGPT Avatar"
              className="me-2"
              style={{
                width: "50px",
                height: "50px",
                borderRadius: "50%",
                objectFit: "contain",
              }}
            />
          </div>
          <div
            className="bg-white border rounded p-4 message-container"
            style={{ maxWidth: "80%" }}
          >
            <div
              className="typing-animation"
              style={{
                display: "flex",
                gap: "2px",
              }}
            >
              <span
                style={{
                  animation: "typingAnimation 1s infinite",
                  display: "inline-block",
                  fontSize: "16px",
                  animationDelay: "0s",
                }}
              >
                .
              </span>
              <span
                style={{
                  animation: "typingAnimation 1s infinite",
                  display: "inline-block",
                  fontSize: "16px",
                  animationDelay: "0.2s",
                }}
              >
                .
              </span>
              <span
                style={{
                  animation: "typingAnimation 1s infinite",
                  display: "inline-block",
                  fontSize: "16px",
                  animationDelay: "0.4s",
                }}
              >
                .
              </span>
              <style>{`
                @keyframes typingAnimation {
                  0% { transform: translateY(0px) scale(1); }
                  50% { transform: translateY(-10px) scale(1.2); }
                  100% { transform: translateY(0px) scale(1); }
                }
              `}</style>
            </div>
          </div>
        </div>
      )}
      <div ref={messagesEndRef} />
    </div>
  );
};

export default ChatConversation;
